"use client";

import React from "react";
import { Microscope, Activity, Sparkles, ShieldCheck, ArrowRight, Check, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const specialties = [{
  id: "couro-cabeludo",
  label: "Couro Cabeludo",
  title: "Distúrbios do Couro Cabeludo",
  icon: Microscope,
  description: "Diagnóstico e tratamento de patologias que afetam a saúde da pele do couro cabeludo, essenciais para o crescimento saudável dos fios.",
  symptoms: ["Oleosidade excessiva", "Caspa e seborréia", "Sensibilidade e coceira", "Psoríase e dermatites"],
  treatments: ["Detox Capilar", "Argiloterapia", "Fotobiomodulação (LED)", "Alta Frequência"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/scalp_analysis_microscope.png",
  category: "Saúde Dermatológica"
}, {
  id: "quedas-alopecias",
  label: "Quedas e Alopécias",
  title: "Tratamento de Quedas e Calvície",
  icon: Activity,
  description: "Protocolos avançados para interromper a queda, estimular o nascimento de novos fios e recuperar a densidade capilar.",
  symptoms: ["Eflúvio Telógeno (pós-parto, stress)", "Calvície (Alopécia Androgenética)", "Alopécia Areata", "Falhas visíveis"],
  treatments: ["MMP (Microinfusão de Medicamentos)", "Laserterapia", "Intradermoterapia", "Tônicos Específicos"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/hair_loss_consultation.png",
  category: "Recuperação Capilar"
}, {
  id: "restauracao",
  label: "Restauração",
  title: "Restauração da Haste Capilar",
  icon: Sparkles,
  description: "Recuperação profunda da estrutura do fio danificado por processos químicos, térmicos ou mecânicos.",
  symptoms: ["Quebra e pontas duplas", "Porosidade extrema", "Corte químico", "Fios elásticos ou rígidos"],
  treatments: ["Reposição Lipídica", "Cauterização a Frio", "Cronograma Capilar Personalizado", "Reconstrução de Aminoácidos"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/hair_restoration_therapy.png",
  category: "Estética e Saúde"
}, {
  id: "prevencao",
  label: "Prevenção",
  title: "Bem-Estar e Manutenção",
  icon: ShieldCheck,
  description: "Cuidados preventivos focados na longevidade dos fios e no relaxamento, unindo saúde e bem-estar.",
  symptoms: ["Manutenção pós-tratamento", "Stress e tensão", "Prevenção do envelhecimento capilar", "Necessidade de nutrição"],
  treatments: ["Spa Capilar", "Massagem Craniana", "Aromaterapia", "Nutrição Preventiva"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/hair_spa_wellness.png",
  category: "Wellness"
}];
export default function EspecialidadesClinicas() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="especialidades-clínicas" className="py-20 bg-background relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-secondary/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

      <div className={`container mx-auto px-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="mb-4 border-primary/20 text-primary bg-primary/5 px-4 py-1 text-sm rounded-full">
            Nossas Especialidades
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
            Soluções Científicas para Saúde Capilar
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Combinamos tecnologia diagnóstica avançada com terapias humanizadas para tratar desde distúrbios do couro cabeludo até a restauração completa dos fios.
          </p>
        </div>

        <Tabs defaultValue="couro-cabeludo" className="flex flex-col lg:flex-row gap-8 lg:gap-12" orientation="vertical">
          {}
          <div className="w-full lg:w-1/3 shrink-0">
            <TabsList className="flex flex-row lg:flex-col h-auto w-full bg-transparent gap-2 p-0 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 scrollbar-hide">
              {specialties.map((item, i) => <TabsTrigger key={item.id} value={item.id} data-index={i} className="group flex items-center justify-start gap-4 px-6 py-4 w-full whitespace-nowrap rounded-xl border border-transparent data-[state=active]:border-border data-[state=active]:bg-card data-[state=active]:shadow-sm hover:bg-muted/50 transition-all duration-300 text-left">
                  <div className="flex items-center justify-center size-10 rounded-full bg-primary/10 text-primary group-data-[state=active]:bg-primary group-data-[state=active]:text-primary-foreground transition-colors">
                    <item.icon className="size-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold text-base text-foreground group-data-[state=active]:text-primary">
                      {item.label}
                    </span>
                    <span className="text-xs text-muted-foreground hidden md:block">
                      {item.category}
                    </span>
                  </div>
                </TabsTrigger>)}
            </TabsList>
            
            <div className="mt-8 hidden lg:block p-6 bg-muted/30 rounded-2xl border border-border/50">
              <h4 className="font-serif font-semibold text-lg mb-2">Precisa de ajuda?</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Agende uma avaliação tricoscópica para descobrir o tratamento ideal para você.
              </p>
              <Button asChild className="w-full" size="lg">
                <Link to="/SchedulingPage">
                  Agendar Consulta
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="w-full lg:w-2/3 min-h-[500px]">
            {specialties.map((item, i) => <TabsContent key={item.id} value={item.id} className="mt-0 h-full animate-in fade-in slide-in-from-right-4 duration-500" data-index={i}>
                <div className="bg-card rounded-3xl border border-border shadow-sm overflow-hidden h-full flex flex-col">
                  {}
                  <div className="relative h-48 md:h-64 w-full overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10" />
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                    <div className="absolute bottom-4 left-6 z-20">
                      <Badge className="mb-2 bg-primary/90 hover:bg-primary text-white border-none">
                        {item.category}
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-serif font-bold text-white">
                        {item.title}
                      </h3>
                    </div>
                  </div>

                  {}
                  <div className="p-6 md:p-8 flex-1 flex flex-col">
                    <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                      {item.description}
                    </p>

                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                      {}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-destructive font-medium mb-2">
                          <CircleAlert className="size-5" />
                          <h4>Sintomas Comuns</h4>
                        </div>
                        <ul className="space-y-3">
                          {item.symptoms.map((symptom, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                              <span className="mt-1.5 size-1.5 rounded-full bg-destructive/60 shrink-0" />
                              {symptom}
                            </li>)}
                        </ul>
                      </div>

                      {}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-primary font-medium mb-2">
                          <Check className="size-5" />
                          <h4>Como Tratamos</h4>
                        </div>
                        <ul className="space-y-3">
                          {item.treatments.map((treatment, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-foreground">
                              <span className="mt-1.5 size-1.5 rounded-full bg-primary shrink-0" />
                              {treatment}
                            </li>)}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-auto pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                      <p className="text-sm text-muted-foreground italic">
                        * Protocolos personalizados após avaliação.
                      </p>
                      <Button asChild variant="default" className="w-full sm:w-auto group">
                        <Link to="/ProtocolsPage">
                          Ver Detalhes do Protocolo
                          <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </div>
          
          {}
          <div className="lg:hidden mt-8">
            <Button asChild className="w-full" size="lg">
              <Link to="/SchedulingPage">
                Agendar Avaliação Troscópica
              </Link>
            </Button>
          </div>
        </Tabs>
      </div>
    </section>;
}